import React, { useState, useRef, useEffect } from 'react';
import './Patients.css';

const HumanBodyDiagram = ({ onBodyPartClick, onDataPointClick, onDotMoved, dataItems = [] }) => {
  const imageRef = useRef(null);
  const wrapperRef = useRef(null);
  const [draggingDot, setDraggingDot] = useState(null); // { originalPos, currentPos, groupKey }

  const handleImageClick = (e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
    if (onBodyPartClick) onBodyPartClick({ x: xPercent, y: yPercent });
  };

  const handleDotMouseDown = (e, group, groupKey) => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingDot({ originalPos: group.position, currentPos: { ...group.position }, groupKey });
  };

  useEffect(() => {
    if (!draggingDot) return;
    const onMove = (e) => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      setDraggingDot(prev => ({ ...prev, currentPos: { x, y } }));
    };
    const onUp = () => {
      if (onDotMoved) onDotMoved(draggingDot.originalPos, draggingDot.currentPos);
      setDraggingDot(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [draggingDot, onDotMoved]);

  // Group data items by click position to show count badges
  const groupedData = dataItems.reduce((acc, item) => {
    if (item.metadata && item.metadata.clickPosition) {
      const key = `${item.metadata.clickPosition.x.toFixed(2)}-${item.metadata.clickPosition.y.toFixed(2)}`;
      if (!acc[key]) {
        acc[key] = {
          position: item.metadata.clickPosition,
          items: []
        };
      }
      acc[key].items.push(item);
    }
    return acc;
  }, {});

  const badgePositions = Object.values(groupedData);

  // Calculate badge position (offset from dot for better visibility)
  const getBadgePosition = (dotX, dotY, index) => {
    // Place badges to the right and slightly up from the dot
    const offsetX = 8; // 8% to the right
    const offsetY = -3; // 3% up
    return {
      x: Math.min(dotX + offsetX, 85), // Don't go beyond 85% to avoid overflow
      y: Math.max(dotY + offsetY, 5)   // Don't go below 5% to avoid overflow
    };
  };

  return (
    <div className="body-diagram-container">
      <div className="body-diagram-wrapper" ref={wrapperRef}>
        <img
          ref={imageRef}
          src="/assets/HumanBody.png"
          alt="Human Body Diagram"
          className="body-diagram-image"
          onClick={handleImageClick}
          style={{ cursor: 'crosshair' }}
        />

        {/* Render SVG for connecting lines */}
        <svg className="body-diagram-svg-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
          {badgePositions.map((group, index) => {
            const dotPos = group.position;
            const badgePos = getBadgePosition(dotPos.x, dotPos.y, index);

            return (
              <line
                key={`line-${index}`}
                x1={dotPos.x}
                y1={dotPos.y}
                x2={badgePos.x}
                y2={badgePos.y}
                className="body-diagram-line"
              />
            );
          })}
        </svg>

        {/* Render red dots and badges for data points */}
        {badgePositions.map((group, index) => {
          const groupKey = `${group.position.x.toFixed(2)}-${group.position.y.toFixed(2)}`;
          const isDragging = draggingDot?.groupKey === groupKey;
          const dotPos = isDragging ? draggingDot.currentPos : group.position;
          const badgePos = getBadgePosition(dotPos.x, dotPos.y, index);
          const dataType = group.items[0].data_type;

          const typeConfig = {
            imaging:  { icon: 'fa-hospital-o', color: '#007bff' },
            image:    { icon: 'fa-image',       color: '#28a745' },
            document: { icon: 'fa-file-text',   color: '#ffc107' },
            table:    { icon: 'fa-table',        color: '#17a2b8' },
            signal:   { icon: 'fa-heartbeat',   color: '#6c757d' },
          };
          const config = typeConfig[dataType] || { icon: 'fa-database', color: '#6c757d' };

          return (
            <React.Fragment key={`group-${index}`}>
              {/* Dot — drag to move, click to preview */}
              <div
                className="body-click-point"
                style={{
                  left: `${dotPos.x}%`, top: `${dotPos.y}%`,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  zIndex: isDragging ? 20 : 10,
                  boxShadow: isDragging ? '0 0 0 3px rgba(255,255,255,0.4)' : undefined,
                }}
                onMouseDown={e => handleDotMouseDown(e, group, groupKey)}
                onClick={e => { e.stopPropagation(); if (!draggingDot) onDataPointClick && onDataPointClick(group.items); }}
              />

              {/* Badge */}
              <div
                className="body-data-badge"
                style={{
                  left: `${badgePos.x}%`, top: `${badgePos.y}%`,
                  backgroundColor: config.color,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  opacity: isDragging ? 0.7 : 1,
                  zIndex: isDragging ? 20 : 10,
                }}
                title={`${group.items.length} ${dataType} file(s) — drag to move, click to preview`}
                onMouseDown={e => handleDotMouseDown(e, group, groupKey)}
                onClick={e => { e.stopPropagation(); if (!draggingDot) onDataPointClick && onDataPointClick(group.items); }}
              >
                <i className={`fa ${config.icon}`}></i>
                <span className="badge-count">{group.items.length}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="body-diagram-label">
        <i className="fa fa-hand-pointer-o me-2"></i>
        Click on the body to add data
      </div>
    </div>
  );
};

export default HumanBodyDiagram;
